import { DynamicColumn, DynamicFieldValue } from './dynamic-framework.models';

export const MAX_WORKFLOW_IMPORT_ROWS = 500;

export interface DynamicWorkflowCsvResult {
  rows: DynamicFieldValue[][];
  errors: string[];
}

export function parseWorkflowCsv(
  source: string,
  fields: readonly DynamicColumn[],
): DynamicWorkflowCsvResult {
  const table = parseCsv(source);
  if (!table.length || !table[0].some(value => value.trim())) {
    return { rows: [], errors: ['The CSV file needs a header row.'] };
  }

  const activeFields = fields.filter(field => field.isActive);
  const headers = table[0].map(normalizeHeader);
  const indexes = new Map<string, number>();
  const errors: string[] = [];
  for (const field of activeFields) {
    const aliases = [normalizeHeader(field.key), normalizeHeader(field.label)];
    const index = headers.findIndex(header => aliases.includes(header));
    if (index >= 0) indexes.set(field.key, index);
    else if (field.isRequired) errors.push(`Missing required column "${field.label}".`);
  }
  if (errors.length) return { rows: [], errors };

  const dataRows = table.slice(1).filter(row => row.some(value => value.trim()));
  if (dataRows.length > MAX_WORKFLOW_IMPORT_ROWS) {
    return {
      rows: [],
      errors: [`A CSV file can contain at most ${MAX_WORKFLOW_IMPORT_ROWS} rows.`],
    };
  }

  const rows: DynamicFieldValue[][] = [];
  dataRows.forEach((row, index) => {
    const rowNumber = index + 2;
    const values: DynamicFieldValue[] = [];
    for (const field of activeFields) {
      const columnIndex = indexes.get(field.key);
      const raw = columnIndex == null ? '' : (row[columnIndex] ?? '').trim();
      if (!raw) {
        if (field.isRequired) errors.push(`Row ${rowNumber}: ${field.label} is required.`);
        continue;
      }
      const parsed = parseValue(field, raw, rowNumber, errors);
      if (parsed) values.push(parsed);
    }
    rows.push(values);
  });
  return errors.length ? { rows: [], errors } : { rows, errors: [] };
}

function parseValue(
  field: DynamicColumn,
  raw: string,
  rowNumber: number,
  errors: string[],
): DynamicFieldValue | null {
  let value: string | number | boolean = raw;
  if (field.dataType === 'number') {
    value = Number(raw);
    if (!Number.isFinite(value)) {
      errors.push(`Row ${rowNumber}: ${field.label} must be a number.`);
      return null;
    }
  } else if (field.dataType === 'boolean') {
    const normalized = raw.toLowerCase();
    if (['true', 'yes', '1'].includes(normalized)) value = true;
    else if (['false', 'no', '0'].includes(normalized)) value = false;
    else {
      errors.push(`Row ${rowNumber}: ${field.label} must be true or false.`);
      return null;
    }
  } else if (field.dataType === 'date') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      errors.push(`Row ${rowNumber}: ${field.label} must use YYYY-MM-DD.`);
      return null;
    }
  } else if (field.dataType === 'choice') {
    const normalized = normalizeHeader(raw);
    const option = field.options.find(item => item.isActive &&
      [normalizeHeader(item.key), normalizeHeader(item.label)].includes(normalized));
    if (!option) {
      errors.push(`Row ${rowNumber}: ${field.label} has an unknown choice.`);
      return null;
    }
    value = option.key;
  }
  return { fieldType: field.dataType, columnKey: field.key, value };
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function parseCsv(source: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let value = '';
  let quoted = false;

  for (let index = 0; index < source.length; index++) {
    const character = source[index];
    if (character === '"') {
      if (quoted && source[index + 1] === '"') {
        value += '"';
        index++;
      } else {
        quoted = !quoted;
      }
    } else if (character === ',' && !quoted) {
      row.push(value);
      value = '';
    } else if ((character === '\n' || character === '\r') && !quoted) {
      if (character === '\r' && source[index + 1] === '\n') index++;
      row.push(value);
      rows.push(row);
      row = [];
      value = '';
    } else {
      value += character;
    }
  }
  if (value.length || row.length) {
    row.push(value);
    rows.push(row);
  }
  return rows;
}
